import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAppDispatch} from 'store/hooks';
import { permissions } from 'utils/dropDownList';
import { emargencyGuardianSchema } from 'utils/schema';
import ReactPhoneInput from 'react-phone-input-material-ui';
import { Restricted } from 'components/Restricted';
import { getPatientContact, updatePatientContact } from 'store/slices/patientInfoSlice';
import { useAuth } from 'utils/AuthContext';
import { useLocation } from 'react-router-dom';

// return Emergency GaurdianInformation component
const EmergencyGaudian = (props: any) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { state } = useLocation();
  //validation schema
  const {
    register,
    control,
    formState: { errors},
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(emargencyGuardianSchema),
    mode: 'onChange',
    shouldFocusError: false, 
  });

  const [disableNextButton, setDisableNextButton] = useState(true);
  const contactSource = watch('contactSource');
  const isGuardianSource = contactSource === 'PRIMARY_SOURCE' || contactSource === 'SECONDARY_SOURCE';
  const isFormInvalid =
  !contactSource ||
  !!errors.contactSource ||
  !!errors.firstName ||
  !!errors.lastName ||
  !!errors.relation ||
  !!errors.mobileNumber;

  const isAdmin = user.isAdmin;
  const patientId = isAdmin ? state?.patientId ?? props?.patientId : user?.patientId;
  const isFormInitialized = useRef(false);
  const [loading, setLoading] = useState(false);

  const onSubmitForm = () => {
    setLoading(true);
    const formData = getValues();
    const contactData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      relation: formData.relation,
      mobileNumber: formData.mobileNumber,
      contactSource: formData.contactSource,
    };
    dispatch(updatePatientContact({ source: formData.contactSource, patientId, contactData }))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          toast.success('Emergency contact updated successfully');
          setLoading(false);
          props.skipStep();
        } 
      })
      .catch((error: any) => {
        toast.error('Failed to update emergency contact');
        setLoading(false);
      }); 
  };

  const handleSkipStep = () => {
    props.skipStep();
  };

  const handleContactSourceChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    
    const source = event.target.value;
    setValue('contactSource', source);
    if(source === '' || source === 'ADD_NEW_CONTACT_SOURCE') {
      setDisableNextButton(true);
      return;
    }
    // Primary / Secondary
    if (source === 'PRIMARY_SOURCE' || source === 'SECONDARY_SOURCE') {
      setLoading(true);
      try {
        const response = await dispatch(
          updatePatientContact({source, patientId, contactData: {},})
        ).unwrap();

        if (response?.status === 200) {
          const contactDetails = response?.data;
          if(contactDetails==='') {
            toast.error('Secondary emergency contact not found');
            setDisableNextButton(true);
            return;
          }
          setValue( 'firstName', contactDetails.firstName ?? '' );
          setValue('lastName', contactDetails.lastName ?? '' );
          setValue( 'relation',  contactDetails.relation ?? '' );
          setValue( 'mobileNumber', contactDetails.mobileNumber ? `1${contactDetails.mobileNumber}` : '1' ); 
            
          setDisableNextButton(false);
          toast.success('Emergency contact updated successfully');
        }
      } catch (error) {
        console.error('Failed to update emergency contact', error);
        toast.error('Failed to update emergency contact');
      } finally { 
        setLoading(false);
      }
    }
  };

  

  useEffect(() => {
    setLoading(true);
    // Fetch patient contact details using patientId
    dispatch(getPatientContact(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) { 
          // Handle successful response and populate the form with contact details
          const contactDetails = response?.data;
          if(contactDetails==='') {
            setDisableNextButton(true);
            return;
          }
          // You can set the form values here using the contactDetails
          setValue('firstName', contactDetails.firstName);
          setValue('lastName', contactDetails.lastName);
          setValue('relation', contactDetails.relation);
          setValue( 'mobileNumber', contactDetails.mobileNumber ? `1${contactDetails.mobileNumber}` : '1' ); 

          setValue('contactSource', contactDetails.contactSource, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          }
          );
          setDisableNextButton(false);
          isFormInitialized.current = true;
        }
      }).finally(() => {
        setLoading(false);
      });
    // You can dispatch an action to fetch the details and populate the form
  }, [dispatch, patientId, setValue]);

  return (
    <>
      <Container component='main' maxWidth='sm' sx={{ padding: '0px !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography
              variant='body1'
              gutterBottom
              sx={{ backgroundColor: '#FFFBF1', padding: '8px 8px 4px 8px' }}
            >
              <b>IMPORTANT:</b> This person will be contacted in case of emergencies if Primary and
              Secondary Guardian do not respond.
            </Typography>
          </Box>

          <Box
            sx={{ mt: 1 }}
            component='form'
          >
            <Grid container spacing={2}>
              {/* Emergency Contact Source */}
              <Grid item xs={12}>
                <InputLabel htmlFor='contactSource'>
                  Emergency Contact Selection*
                </InputLabel>

                <TextField
                  id='contactSource'
                  select
                  fullWidth
                  required
                  value={contactSource ?? ''}
                  SelectProps={{ native: true }}
                  {...register('contactSource', {
                    required: 'Emergency contact source is required',
                    onChange: handleContactSourceChange,
                  })}
                  error={!!errors.contactSource}
                  helperText={
                    errors.contactSource
                      ? String(errors.contactSource.message)
                      : ''
                  }
                >
                  <option value=''>Select from Dropdown</option>
                  <option value='PRIMARY_SOURCE'>
                    Primary Guardian
                  </option>
                  <option value='SECONDARY_SOURCE'>
                    Secondary Guardian
                  </option>
                  <option value='ADD_NEW_CONTACT_SOURCE'>
                    Add / Update Manually
                  </option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='EGfirstName'>
                  First Name*
                </InputLabel>
                <TextField
                  id='EGfirstName'
                  autoComplete='firstName'
                  placeholder='Enter contact’s First Name'
                  required
                  fullWidth
                  autoFocus
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName ? errors.firstName?.message : ''}
                  disabled={isGuardianSource}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='lastName'>
                 Last Name*
                </InputLabel>
                <TextField
                  id='EGlasttName'
                  autoComplete='lastName'
                  placeholder='Enter contact’s Last Name'
                  required
                  fullWidth
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName ? errors.lastName?.message : ''}
                  disabled={isGuardianSource}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='relation'>
                  Relationship to the patient*
                </InputLabel>
                <TextField
                  id='relation'
                  autoComplete='relation'
                  placeholder='Enter relationship to the patient'
                  //select // tell TextField to render select
                  fullWidth
                  {...register('relation')}
                  error={!!errors.relation}
                  helperText={errors.relation ? errors.relation?.message : ''}
                  value={watch('relation') ? watch('relation') : ''}
                  disabled={isGuardianSource}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='mobileNumber'>
                  Phone Number*
                </InputLabel>
                <Controller
                  name="mobileNumber"
                  control={control}
                  render={({ field }) => (
                    <ReactPhoneInput
                      {...field}
                      label=''
                      component={TextField}
                      country={'us'}
                      onlyCountries={['us']}
                      placeholder="+1 (555) 000-0000"
                      disableDropdown
                      countryCodeEditable={false}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                      inputProps={{
                        error: !!errors.mobileNumber,
                        helperText: errors.mobileNumber?.message,
                      }}
                      disabled={isGuardianSource}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {props?.disableSaveButton ? (
              <Grid container>
                <Alert severity='warning'>Please save Primary Guardian Information first.</Alert>
              </Grid>
            ) : (
              ''
            )}

            <Restricted permission={permissions.EDIT_PROFILE}>
              <Grid container justifyContent='center'>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'center',
                      flexDirection: { xs: 'column', sm: 'row' }, // ✅ responsive
                      alignItems: 'center',
                    }}
                  >
                    {/* Skip / Next */}
                    
                    <Button
                      size='small'
                      variant='contained'
                      onClick={() => handleSkipStep()}
                      sx={{
                        minWidth: 140,
                        mt: 2,
                        mb: 2,
                      }}
                      disabled={disableNextButton}
                    >
                        NEXT
                    </Button>
                  

                    {/* Save */}
                    <LoadingButton
                      loading={loading}
                      variant='contained'
                      sx={{
                        minWidth: 140,
                        mt: 2,
                        mb: 2,
                      }}
                      // eslint-disable-next-line max-len
                      disabled={ isFormInvalid || (props?.disableSaveButton ?? false) || isGuardianSource ||  loading
                      }
                      onClick={onSubmitForm}
                    >
  Save
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Restricted>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default EmergencyGaudian;
